import React from "react";
import {
  Button,
  Container,
  IconButton,
  Typography,
} from "@material-ui/core";
import {
  Add,
  ArrowBack,
  DeleteOutline,
  Remove,
} from "@material-ui/icons";
import { useHistory } from "react-router-dom";

import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";

import { Product } from "../../components/Products/ProductCard";

interface CartItem {
  product: Product;
  quantity: number;
}

interface CartProps {
  cart: CartItem[];
  setCart: React.Dispatch<React.SetStateAction<CartItem[]>>;
  cartCount: number;
  onCartClick: () => void;
}

const Cart: React.FC<CartProps> = ({
  cart,
  setCart,
  cartCount,
  onCartClick,
}) => {
  const history = useHistory();

  const updateQuantity = (
    productId: any,
    change: any
  ) => {
    setCart((currentCart) =>
      currentCart.map((item) => {
        if (item.product.id !== productId) {
          return item;
        }

        return {
          ...item,
          quantity: Math.max(
            1,
            item.quantity + change
          ),
        };
      })
    );
  };

  const removeItem = (productId: any) => {
    setCart((currentCart) =>
      currentCart.filter(
        (item) => item.product.id !== productId
      )
    );
  };

  const subtotal = cart.reduce(
    (total, item) =>
      total +
      item.product.price * item.quantity,
    0
  );

  const shipping = subtotal > 50 || subtotal === 0
    ? 0
    : 5;

  const total = subtotal + shipping;

  return (
    <div className="cart-page">

      {/* HEADER */}
      <Header
        cartCount={cartCount}
        onCartClick={onCartClick}
      />

      {/* PAGE TITLE */}
      <section className="cart-page-header">
        <Container maxWidth="lg">
          <div className="cart-title-content">

            <Typography className="cart-eyebrow">
              Your Basket
            </Typography>

            <Typography className="cart-page-title">
              Shopping Cart
            </Typography>

            <Typography className="cart-page-description">
              Review your selected organic products
              before completing your order.
            </Typography>

          </div>
        </Container>
      </section>

      {/* CART CONTENT */}
      <Container maxWidth="lg">
        {cart.length === 0 ? (
          <div className="cart-empty">

            <div className="cart-empty-icon">
              🛒
            </div>

            <Typography className="cart-empty-title">
              Your cart is empty
            </Typography>

            <Typography className="cart-empty-text">
              You haven't added any products to
              your basket yet.
            </Typography>

            <Button
              className="cart-shop-button"
              onClick={() => history.push("/shop")}
            >
              CONTINUE SHOPPING
            </Button>

          </div>
        ) : (
          <div className="cart-layout">

            {/* =========================
                CART PRODUCTS
            ========================= */}
            <div className="cart-products">

              <div className="cart-products-header">

                <Typography>
                  Product
                </Typography>

                <Typography>
                  Price
                </Typography>

                <Typography>
                  Quantity
                </Typography>

                <Typography>
                  Total
                </Typography>

              </div>

              {cart.map((item) => (
                <div
                  className="cart-product-row"
                  key={item.product.id}
                >

                  {/* PRODUCT */}
                  <div className="cart-product-info">

                    <div className="cart-product-image">
                      <img
                        src={item.product.image}
                        alt={item.product.name}
                      />
                    </div>

                    <div>
                      <Typography className="cart-product-name">
                        {item.product.name}
                      </Typography>

                      <Typography className="cart-product-category">
                        Organic Product
                      </Typography>

                      <button
                        className="cart-remove-mobile"
                        onClick={() =>
                          removeItem(
                            item.product.id
                          )
                        }
                      >
                        <DeleteOutline />
                        Remove
                      </button>
                    </div>

                  </div>

                  {/* PRICE */}
                  <Typography className="cart-product-price">
                    ${item.product.price.toFixed(2)}
                  </Typography>

                  {/* QUANTITY */}
                  <div className="cart-quantity">

                    <IconButton
                      size="small"
                      onClick={() =>
                        updateQuantity(
                          item.product.id,
                          -1
                        )
                      }
                    >
                      <Remove />
                    </IconButton>

                    <Typography>
                      {item.quantity}
                    </Typography>

                    <IconButton
                      size="small"
                      onClick={() =>
                        updateQuantity(
                          item.product.id,
                          1
                        )
                      }
                    >
                      <Add />
                    </IconButton>

                  </div>

                  {/* TOTAL */}
                  <Typography className="cart-product-total">
                    $
                    {(
                      item.product.price *
                      item.quantity
                    ).toFixed(2)}
                  </Typography>

                  {/* REMOVE */}
                  <IconButton
                    className="cart-remove-button"
                    onClick={() =>
                      removeItem(
                        item.product.id
                      )
                    }
                  >
                    <DeleteOutline />
                  </IconButton>

                </div>
              ))}

              {/* CONTINUE SHOPPING */}
              <div className="cart-continue">

                <Button
                  startIcon={<ArrowBack />}
                  onClick={() =>
                    history.push("/shop")
                  }
                >
                  CONTINUE SHOPPING
                </Button>

              </div>

            </div>

            {/* =========================
                CART SUMMARY
            ========================= */}
            <div className="cart-summary">

              <Typography className="summary-eyebrow">
                Order Summary
              </Typography>

              <Typography className="summary-title">
                Cart Total
              </Typography>

              <div className="summary-divider" />

              <div className="summary-row">

                <Typography>
                  Subtotal
                </Typography>

                <Typography>
                  ${subtotal.toFixed(2)}
                </Typography>

              </div>

              <div className="summary-row">

                <Typography>
                  Shipping
                </Typography>

                <Typography>
                  {shipping === 0
                    ? "FREE"
                    : `$${shipping.toFixed(2)}`}
                </Typography>

              </div>

              <Typography className="free-shipping-text">
                {subtotal >= 50
                  ? "🎉 You qualify for free shipping!"
                  : "Free shipping on orders over $50"}
              </Typography>

              <div className="summary-divider" />

              <div className="summary-total">

                <Typography>
                  Total
                </Typography>

                <Typography>
                  ${total.toFixed(2)}
                </Typography>

              </div>

              <Button
                fullWidth
                className="checkout-button"
                onClick={() =>
                  history.push("/checkout")
                }
              >
                PROCEED TO CHECKOUT
              </Button>

              <Button
                fullWidth
                className="summary-shop-button"
                onClick={() =>
                  history.push("/shop")
                }
              >
                CONTINUE SHOPPING
              </Button>

            </div>

          </div>
        )}
      </Container>

      <Footer />

    </div>
  );
};

export default Cart;